import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Features.css';

const Features: React.FC = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const features = [
    {
      icon: '🤖',
      title: 'AI-Powered Test Generation',
      description: 'Automatically create comprehensive tests from your documents using advanced AI. Simply provide your training materials and let Prepwise generate questions tailored to your content.'
    },
    {
      icon: '📊',
      title: 'Real-Time Results Tracking',
      description: 'Monitor employee performance with detailed analytics. View test scores, completion rates, and identify knowledge gaps across your organization.'
    },
    {
      icon: '💬',
      title: 'Intelligent Chat Assistant',
      description: 'Get instant answers from your knowledge base. Employees can ask questions and receive accurate responses based on your company documents.'
    },
    {
      icon: '✅',
      title: 'Smart Grading System',
      description: 'Advanced grading with fuzzy matching, partial credit, and keyword detection. Short-answer questions are automatically evaluated for accuracy.'
    },
    {
      icon: '👥',
      title: 'Role-Based Access Control',
      description: 'Separate admin and employee dashboards. Admins create and manage tests while employees focus on taking assessments and accessing resources.'
    },
    {
      icon: '📝',
      title: 'Flexible Question Types',
      description: 'Support for multiple-choice, true/false, short-answer, and essay questions. Customize difficulty levels and point values for each question.'
    }
  ];

  const faqs = [
    {
      question: 'What is Prepwise?',
      answer: 'Prepwise is an AI-powered test management platform designed to help organizations create, distribute, and track employee assessments. It simplifies the process of knowledge testing by automatically generating questions from your training materials and providing detailed analytics on employee performance.'
    },
    {
      question: 'How does AI test generation work?',
      answer: 'Prepwise uses advanced language models to analyze your uploaded documents and generate relevant test questions. Simply select a topic or subject area, and our AI will create questions based solely on the content in your knowledge base, ensuring accuracy and relevance.'
    },
    {
      question: 'Can employees retake tests?',
      answer: 'Yes, employees can retake tests as needed. However, each submission is tracked separately, allowing admins to monitor progress over time and see improvement in employee knowledge.'
    },
    {
      question: 'How secure is employee data?',
      answer: 'Prepwise takes data security seriously. All test results and employee information are stored securely in our database. Access is controlled through role-based permissions, ensuring only authorized personnel can view sensitive data.'
    },
    {
      question: 'Can I customize test questions?',
      answer: 'Absolutely! After AI generates questions, admins can review, edit, add, or remove any questions. You have full control over test content, difficulty, and grading criteria before publishing to employees.'
    },
    {
      question: 'Does Prepwise integrate with other systems?',
      answer: 'Currently, Prepwise operates as a standalone platform. We\'re continuously working on integrations with popular HR and learning management systems. Contact us to discuss specific integration needs.'
    }
  ];

  return (
    <div className="features-page">

      {/* Hero Section */}
      <section className="features-hero">
        <div className="hero-content">
          <p className="hero-subtitle">Best Test Management Platform</p>
          <h1 className="hero-headline">
            Powerful features from{' '}
            <span className="highlight-button">Prepwise</span>
          </h1>
          <p className="hero-description">
            Streamline your organization's training and assessment process with our intuitive, 
            AI-powered platform. Designed for modern enterprises, our solutions simplify test 
            creation, employee evaluation, and knowledge management.
          </p>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="key-features-section">
        <div className="section-header">
          <p className="section-label">Key Features</p>
          <h2 className="section-title">Boost your training with Prepwise</h2>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-content">
          <div className="benefits-header">
            <p className="section-label">Benefits</p>
            <h2 className="section-title">Empower your training operations</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">📤</div>
              <h3 className="benefit-title">Flexible Test Management</h3>
              <p className="benefit-description">
                Easily create, edit, and publish tests with our intuitive admin dashboard. 
                Organize tests by topic, track versions, and schedule publication dates.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📈</div>
              <h3 className="benefit-title">Custom Analytics & Reporting</h3>
              <p className="benefit-description">
                Create detailed reports on employee performance, test completion rates, 
                and knowledge gaps. Export data for further analysis or presentations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-content">
          <div className="faq-header">
            <p className="section-label">FAQ</p>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className={`faq-question ${openFaq === index ? 'open' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <p className="trust-text">Trusted by organizations worldwide</p>
        <div className="trust-logos">
          <div className="trust-logo">Enterprise</div>
          <div className="trust-logo">Education</div>
          <div className="trust-logo">Corporate</div>
          <div className="trust-logo">Startups</div>
          <div className="trust-logo">Training</div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to transform your training process?</h2>
          <p className="cta-description">
            Join organizations using Prepwise to streamline their employee assessments
          </p>
          <button className="cta-button" onClick={() => navigate('/login')}>
            Get Started Free
          </button>
        </div>
      </section>
    </div>
  );
};

export default Features;

